/**
 * **币种呈现（符号表 + 金额文本）—— 🔴 全项目唯一源**
 *
 * 依据：**`DR-036`**（多币种金额呈现约定定稿 · D1–D5）。
 *
 * ## 为什么需要这个文件
 *
 * `DR-004` 定「余额永远**原币**整数分、**不跨币相加**」⇒ **币种是金额的一部分，不是装饰**。
 * 但在此之前，端侧只知道「元」：`Amount` 组件把 `¥` 写死，于是
 * 一个美元账户的余额会被渲染成人民币的样子 —— 那是**用沉默制造错误信息**。
 *
 * ⇒ 币种 → 符号的映射只在本文件定义一次；**Web / 小程序 / 服务端文案只消费**。
 *
 * ## 🔴 三条不可协商的约定（逐条对应 `DR-036`）
 *
 * | # | 约定 | 不这么做会怎样 |
 * |:--:|---|---|
 * | **D3** | 人民币 = `¥`、**日元 = `円`**（用**汉字**，不用 `JP¥`） | ISO 4217 **不定义货币符号** ⇒ `¥` 天然歧义；不消解则"数字看起来一样、含义不同" |
 * | **D4** | 符号顺序 = **币种符 → 数值符号 → 数字（千分位）** | 混排顺序会让同一列表里两种写法并存（`−¥300.00` vs `¥-300.00`） |
 * | **D5** | 🔴 **未知币种明示回退**（`SGD 300.00`）；**绝不静默显示成 `¥`** | 把外币伪装成人民币，是最坏的一种"静默失败" |
 *
 * ## D5 为什么是**必需**而非防御（实测依据）
 *
 * `contract/account.ts` 的 `CurrencySchema` **只校验"三个字母"、没有白名单** ⇒
 * 用户可以建出任意三字母币种；叠加"ISO 4217 不定义符号"⇒ **符号表注定不完备**，
 * 「查不到符号」是**必然发生的正常路径**，不是异常路径。
 *
 * ## ⚠️ 已知边界（不在本文件解决）
 *
 * - **小数位恒为 2**：本项目金额一律存"整数**分**"（`UNIT_SCALE = 100`），
 *   故 `円1,200.00` 也带 `.00` —— 这是**存储模型的必然结果**，不是本文件的疏漏。
 *   （零小数币种如 JPY 的显示形态属另一议题，`DR-036` §5 未纳入。）
 * - **不做折算展示**（如"折合人民币 ¥X"）—— 那属 `DR-004` 的折算场景。
 */

import type { Cents } from '../types/brand.js';
import { centsToYuanText } from './money.js';

// ─────────────────────────── 千分位 ───────────────────────────

/**
 * 千分位分组（**纯字符串操作** —— 端侧禁止对金额做任何算术，规格 §22.1）。
 *
 * 入参可带前导 ASCII `-`（原样保留）；⚠️ `formatMoneyText` 传的是**已去符号**的文本，
 * 因为符号的**字形与位置**由它统一决定（见 `MINUS`）。
 */
export function groupThousands(text: string): string {
  const negative = text.startsWith('-');
  const body = negative ? text.slice(1) : text;
  const [intPart = '', fracPart = ''] = body.split('.');
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${negative ? '-' : ''}${grouped}${fracPart === '' ? '' : `.${fracPart}`}`;
}

// ─────────────────────────── 符号表 ───────────────────────────

/**
 * 🔴 **币种 → 符号**（唯一源；`DR-036` D2）。
 *
 * ⚠️ 本表**注定不完备**（ISO 4217 不定义符号 + 无币种白名单）⇒
 * 查不到时必须走 `D5` 回退，**不是**"补全本表"。
 *
 * 选表判据：中国家庭的常见持有币种。**不求全**（求全反而会让人误以为"表里没有就是不需要回退"）。
 */
export const CURRENCY_SYMBOLS: Readonly<Record<string, string>> = {
  // 🔴 D3：¥ 归人民币；日元用汉字「円」消解歧义
  CNY: '¥',
  JPY: '円',
  USD: '$',
  EUR: '€',
  GBP: '£',
  HKD: 'HK$',
  MOP: 'MOP$',
  TWD: 'NT$',
  SGD: 'S$',
  AUD: 'A$',
  CAD: 'C$',
  NZD: 'NZ$',
  KRW: '₩',
  THB: '฿',
  MYR: 'RM',
  CHF: 'CHF',
};

/**
 * 🔴 **排版减号 `−`（U+2212）**，不是 ASCII `-`。
 *
 * 依据 = **设计原型**（`docs/03-UX与UI设计/02-账_流水_原型.html` 599/600/612 与
 * `assets/_chrome.js` 的 `CNY()` 助手）用的就是这个字形；等宽字体下它是**居中**的，
 * 而 ASCII `-` 偏上、与下划线/连字符混淆。
 *
 * ⚠️ 共享内核 `centsToYuanText()` 产出的是 **ASCII `-`**（那是"数据文本"，供 `Number()` 解析）——
 * 本文件负责把它换成**展示字形**，**只换一处**（否则就是第二份真相）。
 */
const MINUS = '\u2212';

/** 正号：ASCII `+`（与原型一致；`+` 无字形歧义，不需要排版替换） */
const PLUS = '+';

/**
 * 币种**符号** —— 未知币种返回 **ISO 代码本身**（无空格）。
 *
 * 用途 = **不需要拼接金额**的场合（输入框 adornment、表格列头）。
 * 需要"符号 + 金额"时用 `formatMoneyText()`（它会给未知币种补空格，见 `D5`）。
 */
export function currencySymbolOf(currency: string): string {
  const code = normalizeCode(currency);
  return CURRENCY_SYMBOLS[code] ?? code;
}

/** 该币种是否**有**符号（即是否走 `D5` 回退）—— 供 UI 决定是否提示"使用代码显示" */
export function hasCurrencySymbol(currency: string): boolean {
  return CURRENCY_SYMBOLS[normalizeCode(currency)] !== undefined;
}

/**
 * 归一化币种代码。
 *
 * ⚠️ 服务端 `CurrencySchema` 保证"三个字母"，但**不保证大小写** ⇒ 统一大写。
 * 空串是**不该发生**的（契约拒绝），但不静默产出空前缀 —— 用 `???` 让它在界面上**看得见**。
 */
function normalizeCode(currency: string): string {
  return currency.trim().toUpperCase() || '???';
}

// ─────────────────────────── 金额文本 ───────────────────────────

export interface MoneyTextOptions {
  /**
   * **有方向的金额**（收入 / 支出）⇒ 正数显示 `+`。
   *
   * 🔴 默认 `false`：净资产 / 总资产 / 余额 / 保额这类**绝对额**加 `+` 是噪音
   * （`¥+9,700.00` 读起来像"增加了 9700"）。设计原型对"选中合计"也是**显式传
   * `{plus:true}` 才加**（`_chrome.js` 的 `CNY()`）⇒ 这是原型的既有语义，不是新发明。
   */
  signed?: boolean;
  /** 千分位（默认 `true`；图表 Y 轴那种窄格子可关掉） */
  thousands?: boolean;
}

/**
 * 🔴 **金额的展示文本** —— 端侧唯一入口（`Amount` 组件、图表 Tooltip、行内文案都调它）。
 *
 * 排列（`DR-036` D4）：**币种符 → 数值符号 → 数字（千分位）**，例如：
 *
 * | 场合 | 输出 |
 * |---|---|
 * | 支出（本币) | `¥−300.00` |
 * | 收入（本币） | `¥+3,120.00` |
 * | 零 | `¥0.00` |
 * | 净资产（绝对额） | `¥9,700.00` |
 * | 负净资产（绝对额） | `¥−1,000.00` |
 * | 🔴 未知币种（`D5`） | `SGD 300.00` / `SGD +300.00` |
 *
 * 🔴 **不在这里做任何算术**：数值文本由 `centsToYuanText()`（定点白名单）产出，
 * 本函数只负责**取符号 + 拼接 + 分组**。
 */
export function formatMoneyText(
  cents: Cents,
  currency: string,
  options: MoneyTextOptions = {},
): string {
  const { signed = false, thousands = true } = options;
  const n = cents as number;
  // ⚠️ 必须先取绝对值：`centsToYuanText` 自带 ASCII `-`，直接拼接会得到 `¥--300.00`
  const raw = centsToYuanText((n < 0 ? -n : n) as Cents);
  const digits = thousands ? groupThousands(raw) : raw;
  const sign = n < 0 ? MINUS : n > 0 && signed ? PLUS : '';
  return `${prefixOf(currency)}${sign}${digits}`;
}

/**
 * 展示前缀：**有符号 ⇒ 符号**；**未知币种 ⇒ `代码 + 半角空格`**（`D5`）。
 *
 * 空格是 `D5` 的硬要求（`SGD 300.00`）—— 不加空格会连成 `SGD300.00`，
 * 看起来像一个不存在的混合币种。
 */
function prefixOf(currency: string): string {
  const code = normalizeCode(currency);
  const symbol = CURRENCY_SYMBOLS[code];
  return symbol !== undefined ? symbol : `${code} `;
}
